/**
 * Zustand store for managing live transcript state.
 * Stores user and AI speech during the conversation.
 */

import { create } from "zustand";

export interface TranscriptEntry {
  id: string;
  role: "user" | "ai";
  text: string;
  timestamp: Date;
}

interface TranscriptState {
  transcripts: TranscriptEntry[];
  startTime: Date | null;
  addTranscript: (entry: Omit<TranscriptEntry, "id" | "timestamp">) => void;
  updateLastTranscript: (role: "user" | "ai", text: string) => void;
  clearTranscripts: () => void;
  setStartTime: (time: Date) => void;
  getTranscriptsSnapshot: () => { transcripts: TranscriptEntry[]; startTime: Date | null };
}

export const useTranscriptStore = create<TranscriptState>((set, get) => ({
  transcripts: [],
  startTime: null,

  addTranscript: (entry) => {
    const state = get();
    const startTime = state.startTime || new Date();

    const newEntry: TranscriptEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    set((s) => ({
      transcripts: [...s.transcripts, newEntry],
      startTime: s.startTime || startTime,
    }));
  },

  updateLastTranscript: (role, text) => {
    set((state) => {
      const lastIndex = state.transcripts.length - 1;
      if (lastIndex >= 0 && state.transcripts[lastIndex].role === role) {
        const updated = [...state.transcripts];
        updated[lastIndex] = { ...updated[lastIndex], text: updated[lastIndex].text + text };
        return { transcripts: updated };
      }
      return {
        transcripts: [...state.transcripts, {
          role,
          text,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
        }],
        startTime: state.startTime || new Date(),
      };
    });
  },

  clearTranscripts: () => {
    set({ transcripts: [], startTime: null });
  },

  setStartTime: (time) => {
    set({ startTime: time });
  },

  getTranscriptsSnapshot: () => {
    const { transcripts, startTime } = get();
    return { transcripts: [...transcripts], startTime };
  },
}));
